/**
 * Application Designer — the closed set of cosmetic properties.
 *
 * Phase 1 of the Application Designer is presentation only, and this module is
 * the boundary that keeps it that way. A saved layout may carry cosmetics, so
 * the stored value is untrusted input: every property here has a type, a
 * validator and a default, and anything that is not on this list is dropped on
 * read and on write. That is the same contract `patientSummaryLayout` already
 * applies to card ids, extended to style.
 *
 * Two rules make the set safe to persist:
 *
 *   1. No property ever holds raw CSS. A colour is `#RRGGBB`, a size is a
 *      member of a named scale, everything else is a member of a frozen
 *      enumeration. There is no key a caller can use to smuggle a CSS
 *      declaration, a URL or a font file into the page.
 *   2. Nothing is emitted as an inline style object. A value becomes a CSS
 *      custom property plus a marker attribute, and `emr-application-designer.css`
 *      decides what those mean. A property the stylesheet does not implement
 *      therefore does nothing at all, rather than something unreviewed.
 *
 * Cosmetics are also SPARSE. `sanitizeCosmetics` keeps only the properties that
 * actually differ from the native default and returns null when none do, so a
 * page that has never been styled stores no `style` key and its saved payload
 * keeps exactly the shape it had before the Application Designer existed.
 *
 * Native-card box cosmetics (`cardWidth`, `cardHeight`, `cardAlign`,
 * `cardNudgeY`) are on this allowlist so they persist the same way as padding,
 * but they are opt-in per native element. The properties panel and the
 * per-element sanitizer keep them off every card until that card declares them.
 * They are tokens, not pixels, and they never become FLOW column spans.
 */

const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;

export const BORDER_SIDES = Object.freeze(["top", "right", "bottom", "left"]);

/** Named scales. A stored value is a token; the pixel value lives here only. */
const RADIUS_SCALE = Object.freeze({
  none: "0",
  sm: "3px",
  md: "6px",
  lg: "12px",
  pill: "999px",
});

const PADDING_SCALE = Object.freeze({
  none: "0",
  xs: "4px",
  sm: "8px",
  md: "14px",
  lg: "20px",
});

const GAP_SCALE = Object.freeze({
  none: "0",
  xs: "4px",
  sm: "8px",
  md: "12px",
  lg: "20px",
});

const SHADOW_SCALE = Object.freeze({
  none: "none",
  sm: "0 1px 2px rgba(15, 23, 42, 0.08)",
  md: "0 2px 6px rgba(15, 23, 42, 0.12)",
  lg: "0 6px 16px rgba(15, 23, 42, 0.18)",
});

const TEXT_SIZE_SCALE = Object.freeze({ sm: "12px", md: "14px", lg: "16px" });
const TITLE_SIZE_SCALE = Object.freeze({ sm: "12px", md: "14px", lg: "18px" });

const FONT_FAMILY_SCALE = Object.freeze({
  inherit: "inherit",
  system: "Arial, Helvetica, sans-serif",
  serif: "Georgia, 'Times New Roman', serif",
  mono: "'SFMono-Regular', Consolas, 'Liberation Mono', monospace",
});

const WEIGHT_SCALE = Object.freeze({
  normal: "400",
  medium: "500",
  semibold: "600",
  bold: "700",
});

const ALIGN_SCALE = Object.freeze({ left: "left", center: "center", right: "right" });

/**
 * Native-card box tokens. These are still cosmetics — named tokens, never
 * pixels or column spans — and they never become FLOW geometry. Width stays
 * inside the card's own column; height is a min-height floor so clinical
 * content is not clipped; align and nudge move the box inside that column
 * without changing its column or ordinal.
 */
const CARD_WIDTH_SCALE = Object.freeze({
  narrow: "58%",
  medium: "78%",
  wide: "100%",
});

const CARD_HEIGHT_SCALE = Object.freeze({
  short: "112px",
  medium: "176px",
  tall: "256px",
});

const CARD_ALIGN_SCALE = Object.freeze({
  left: "0 auto",
  center: "auto",
  right: "auto 0",
});

const CARD_NUDGE_Y_SCALE = Object.freeze({
  up: "-12px",
  down: "12px",
});

const BORDER_STYLE_SCALE = Object.freeze({
  solid: "solid",
  dashed: "dashed",
  dotted: "dotted",
  none: "none",
});

function colorProperty(key, label, group, cssVar) {
  return Object.freeze({
    key,
    label,
    group,
    type: "color",
    cssVar,
    default: null,
    sanitize(value) {
      if (typeof value !== "string") return null;
      const trimmed = value.trim();
      return HEX_COLOR.test(trimmed) ? trimmed.toLowerCase() : null;
    },
    toCss(value) {
      return value;
    },
  });
}

function tokenProperty(key, label, group, cssVar, scale, extras = {}) {
  const options = Object.freeze(Object.keys(scale));
  return Object.freeze({
    key,
    label,
    group,
    type: "token",
    options,
    cssVar,
    default: null,
    /** Opt-in properties stay off the panel until a native element declares them. */
    optIn: extras.optIn === true,
    sanitize(value) {
      if (typeof value !== "string") return null;
      const trimmed = value.trim();
      return Object.prototype.hasOwnProperty.call(scale, trimmed) ? trimmed : null;
    },
    toCss(value) {
      return scale[value];
    },
  });
}

function integerProperty(key, label, group, cssVar, min, max, unit = "px") {
  return Object.freeze({
    key,
    label,
    group,
    type: "integer",
    min,
    max,
    cssVar,
    default: null,
    sanitize(value) {
      const parsed = Number.parseInt(value, 10);
      if (!Number.isFinite(parsed)) return null;
      if (parsed < min || parsed > max) return null;
      return parsed;
    },
    toCss(value) {
      return `${value}${unit}`;
    },
  });
}

function booleanProperty(key, label, group, { whenFalse }) {
  return Object.freeze({
    key,
    label,
    group,
    type: "boolean",
    cssVar: null,
    default: true,
    sanitize(value) {
      if (value === false) return false;
      if (value === true) return true;
      return null;
    },
    /** Only the non-default state is stored, so only that state needs CSS. */
    toCss() {
      return whenFalse;
    },
  });
}

/**
 * Which edges draw a border. Stored as a sorted, de-duplicated subset so the
 * same visual choice always serializes identically; null means every edge.
 */
const borderSidesProperty = Object.freeze({
  key: "borderSides",
  label: "Border Sides",
  group: "Border",
  type: "sides",
  options: BORDER_SIDES,
  cssVar: null,
  default: null,
  sanitize(value) {
    if (!Array.isArray(value)) return null;
    const kept = BORDER_SIDES.filter((side) => value.includes(side));
    if (kept.length === 0 || kept.length === BORDER_SIDES.length) return null;
    return kept;
  },
  toCss(value) {
    return value.join(" ");
  },
});

export const COSMETIC_PROPERTIES = Object.freeze([
  colorProperty("backgroundColor", "Background Color", "Surface", "--ad-background"),
  tokenProperty("cornerRadius", "Corner Radius", "Surface", "--ad-corner-radius", RADIUS_SCALE),
  tokenProperty("shadow", "Shadow", "Surface", "--ad-shadow", SHADOW_SCALE),

  colorProperty("borderColor", "Border Color", "Border", "--ad-border-color"),
  /**
   * No custom property of its own: a thickness only ever reaches the page through
   * the four per-side variables `borderSideWidths` derives, so that turning an
   * edge off and setting a thickness are one declaration rather than two.
   */
  integerProperty("borderWidth", "Border Thickness", "Border", null, 0, 8),
  tokenProperty("borderStyle", "Border Style", "Border", "--ad-border-style", BORDER_STYLE_SCALE),
  borderSidesProperty,

  tokenProperty("padding", "Padding", "Spacing", "--ad-padding", PADDING_SCALE),
  tokenProperty("marginBottom", "Spacing Below", "Spacing", "--ad-margin-bottom", GAP_SCALE),

  colorProperty("textColor", "Text Color", "Typography", "--ad-text-color"),
  tokenProperty("fontFamily", "Font", "Typography", "--ad-font-family", FONT_FAMILY_SCALE),
  tokenProperty("fontSize", "Text Size", "Typography", "--ad-font-size", TEXT_SIZE_SCALE),
  tokenProperty("textAlign", "Text Align", "Typography", "--ad-text-align", ALIGN_SCALE),

  booleanProperty("showTitle", "Show Title", "Title", { whenFalse: "none" }),
  tokenProperty("titleSize", "Title Size", "Title", "--ad-title-size", TITLE_SIZE_SCALE),
  tokenProperty("titleWeight", "Title Weight", "Title", "--ad-title-weight", WEIGHT_SCALE),
  tokenProperty("titleAlign", "Title Align", "Title", "--ad-title-align", ALIGN_SCALE),

  tokenProperty("cardWidth", "Card Width", "Card", "--ad-card-width", CARD_WIDTH_SCALE, {
    optIn: true,
  }),
  tokenProperty("cardHeight", "Card Height", "Card", "--ad-card-height", CARD_HEIGHT_SCALE, {
    optIn: true,
  }),
  tokenProperty("cardAlign", "Card Align", "Card", "--ad-card-align", CARD_ALIGN_SCALE, {
    optIn: true,
  }),
  tokenProperty("cardNudgeY", "Card Nudge", "Card", "--ad-card-nudge-y", CARD_NUDGE_Y_SCALE, {
    optIn: true,
  }),
]);

export const COSMETIC_KEYS = Object.freeze(COSMETIC_PROPERTIES.map((property) => property.key));

/** The four native-card box cosmetics. An element must declare each one to use it. */
export const OPT_IN_COSMETIC_KEYS = Object.freeze(
  COSMETIC_PROPERTIES.filter((property) => property.optIn).map((property) => property.key),
);

const PROPERTY_BY_KEY = new Map(COSMETIC_PROPERTIES.map((property) => [property.key, property]));

export function cosmeticProperty(key) {
  return PROPERTY_BY_KEY.get(key) || null;
}

/** Declaration order is the panel order; the group order follows from it. */
export const COSMETIC_GROUPS = Object.freeze(
  COSMETIC_PROPERTIES.reduce((groups, property) => {
    if (!groups.includes(property.group)) groups.push(property.group);
    return groups;
  }, []),
);

export function cosmeticPropertiesInGroup(group) {
  return COSMETIC_PROPERTIES.filter((property) => property.group === group);
}

/**
 * Keeps the valid, non-default cosmetics and drops everything else. Returns
 * null rather than `{}` so a caller can omit the key entirely: an unstyled
 * element must not grow a `style` property just by being normalized.
 */
export function sanitizeCosmetics(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const kept = {};
  COSMETIC_PROPERTIES.forEach((property) => {
    if (!Object.prototype.hasOwnProperty.call(raw, property.key)) return;
    const value = property.sanitize(raw[property.key]);
    if (value === null || value === property.default) return;
    kept[property.key] = value;
  });
  return Object.keys(kept).length ? kept : null;
}

export function hasCosmetics(style) {
  return Boolean(sanitizeCosmetics(style));
}

export function isOptInCosmetic(key) {
  return OPT_IN_COSMETIC_KEYS.includes(key);
}

/**
 * Same as `sanitizeCosmetics`, then drops any opt-in box cosmetic the element
 * has not declared. Persistence still uses the global allowlist for the value
 * itself; this is the per-element gate so Vital Signs can opt in without every
 * other card growing the same controls.
 */
export function sanitizeElementCosmetics(style, acceptedOptIn = []) {
  const sanitized = sanitizeCosmetics(style);
  if (!sanitized) return null;
  const allowed = new Set(acceptedOptIn || []);
  const kept = {};
  Object.entries(sanitized).forEach(([key, value]) => {
    if (isOptInCosmetic(key) && !allowed.has(key)) return;
    kept[key] = value;
  });
  return Object.keys(kept).length ? kept : null;
}

/**
 * Applies one cosmetic edit. Clearing a property, or setting it back to the
 * native default, removes it — which is how "reset this property" works without
 * a second verb.
 *
 * A value that fails validation is REFUSED rather than treated as a clear. An
 * invalid value is not a request to erase what the user already chose, so a
 * malformed colour leaves the previous colour in place.
 */
export function withCosmetic(style, key, value) {
  const property = cosmeticProperty(key);
  if (!property) return sanitizeCosmetics(style);
  const next = { ...(sanitizeCosmetics(style) || {}) };
  const clearing = value === null || value === undefined;
  const sanitized = clearing ? null : property.sanitize(value);
  if (!clearing && sanitized === null) return Object.keys(next).length ? next : null;
  if (sanitized === null || sanitized === property.default) delete next[key];
  else next[key] = sanitized;
  return Object.keys(next).length ? next : null;
}

export function cosmeticValue(style, key) {
  const property = cosmeticProperty(key);
  if (!property) return null;
  const sanitized = sanitizeCosmetics(style);
  if (!sanitized || !Object.prototype.hasOwnProperty.call(sanitized, key)) return property.default;
  return sanitized[key];
}

/**
 * Thickness expanded onto the four edges, with the edges the user turned off
 * emitted as 0px. Choosing edges without choosing a thickness implies the
 * thinnest visible one, so "which sides are visible" is a usable property on its
 * own rather than inert until a thickness is also picked.
 */
function borderSideWidths(style) {
  const width = cosmeticValue(style, "borderWidth");
  const sides = cosmeticValue(style, "borderSides");
  if (width === null && sides === null) return {};
  const thickness = width === null ? 1 : width;
  const active = sides || BORDER_SIDES;
  return BORDER_SIDES.reduce((vars, side) => {
    vars[`--ad-border-${side}-width`] = active.includes(side) ? `${thickness}px` : "0px";
    return vars;
  }, {});
}

/**
 * The CSS custom properties for one element's cosmetics, and the marker
 * attributes that switch the matching stylesheet rules on.
 *
 * A property is inert until `emr-application-designer.css` claims its marker,
 * so the stylesheet — not the stored layout — decides what a cosmetic does.
 */
export function cosmeticStyleProps(style) {
  const sanitized = sanitizeCosmetics(style);
  if (!sanitized) return { style: undefined, attrs: {} };
  const vars = {};
  const attrs = {};
  Object.entries(sanitized).forEach(([key, value]) => {
    const property = cosmeticProperty(key);
    if (!property) return;
    attrs[`data-ad-${dashCase(key)}`] = "";
    if (property.cssVar) vars[property.cssVar] = property.toCss(value);
  });
  if (Object.prototype.hasOwnProperty.call(sanitized, "showTitle")) {
    vars["--ad-title-display"] = cosmeticProperty("showTitle").toCss(false);
  }
  Object.assign(vars, borderSideWidths(sanitized));
  return { style: Object.keys(vars).length ? vars : undefined, attrs };
}

function dashCase(value) {
  return String(value).replace(/[A-Z]/g, (char) => `-${char.toLowerCase()}`);
}
